export type AdminSectionKey =
  | "dashboard"
  | "analytics"
  | "businesses"
  | "approvals"
  | "featured"
  | "categories"
  | "subcategories"
  | "detailed-categories"
  | "category-aliases"
  | "locations"
  | "tourist-spots"
  | "users"
  | "owners"
  | "crm-pipeline"
  | "crm-contacts"
  | "leads"
  | "orders"
  | "invoices"
  | "finance"
  | "reviews"
  | "ads"
  | "ad-placements"
  | "subscriptions"
  | "plans"
  | "seo"
  | "media"
  | "settings";

export interface AdminSection {
  key: AdminSectionKey;
  label: string;
  icon: string;
  group: string;
  desc: string;
}

export const ADMIN_GROUPS = [
  "Overview",
  "Listings",
  "Directory",
  "People",
  "CRM",
  "ERP & Finance",
  "Engagement",
  "Monetization",
  "System",
] as const;

export const ADMIN_SECTIONS: AdminSection[] = [
  { key: "dashboard",     label: "Dashboard",           icon: "ri-dashboard-3-line",   group: "Overview",      desc: "Platform health at a glance — businesses, growth, revenue, leads, reviews and engagement." },
  { key: "analytics",     label: "Analytics",           icon: "ri-bar-chart-2-line",   group: "Overview",      desc: "Trend charts for leads, listings, orders and share activity." },

  { key: "businesses",    label: "Businesses",          icon: "ri-store-2-line",       group: "Listings",      desc: "Every listing on the platform — search, filter, moderate and manage." },
  { key: "approvals",     label: "Business Approvals",  icon: "ri-list-check-3",       group: "Listings",      desc: "Review and publish newly submitted business listings." },
  { key: "featured",      label: "Featured Listings",   icon: "ri-star-line",          group: "Listings",      desc: "Control which listings are promoted as featured placements." },

  { key: "categories",    label: "Categories",          icon: "ri-apps-2-line",        group: "Directory",     desc: "Manage top-level business categories, icons and visibility." },
  { key: "subcategories", label: "Subcategories",       icon: "ri-node-tree",          group: "Directory",     desc: "Manage the subcategories nested under each category." },
  { key: "detailed-categories", label: "A–Z Categories",  icon: "ri-list-ordered-2",     group: "Directory",     desc: "Manage the detailed A–Z business-category index shown across the site — edit, reorder or hide entries without code." },
  { key: "category-aliases",   label: "Category Aliases", icon: "ri-link-m",             group: "Directory",     desc: "Map any A–Z category name to a branded category page so it links straight through — no code required." },
  { key: "locations",     label: "Locations",           icon: "ri-map-pin-2-line",     group: "Directory",     desc: "Manage districts and towns used across the directory." },
  { key: "tourist-spots", label: "Tourist Spots",       icon: "ri-landscape-line",     group: "Directory",     desc: "Curate places to visit — beach, temple, nature and heritage spots shown across the site." },

  { key: "users",         label: "Users",               icon: "ri-group-line",         group: "People",        desc: "All registered accounts — users, owners and administrators." },
  { key: "owners",        label: "Business Owners",     icon: "ri-user-star-line",     group: "People",        desc: "Accounts that own one or more business listings." },

  { key: "crm-pipeline",  label: "Sales Pipeline",      icon: "ri-flow-chart",         group: "CRM",           desc: "Drag-free deal board — move contacts and customers through every sales stage." },
  { key: "crm-contacts",  label: "Contacts & Customers", icon: "ri-contacts-book-2-line", group: "CRM",         desc: "Every contact, prospect and paying customer in one searchable directory." },
  { key: "leads",         label: "Lead Tracking",       icon: "ri-mail-open-line",     group: "CRM",           desc: "Platform-wide lead and enquiry inbox with read status tracking." },

  { key: "orders",        label: "Orders",              icon: "ri-shopping-bag-3-line", group: "ERP & Finance", desc: "Listing-plan and service orders with payment and fulfilment status." },
  { key: "invoices",      label: "Invoices & Billing",  icon: "ri-file-list-3-line",   group: "ERP & Finance", desc: "Issue, send and settle invoices for listing-plan subscriptions." },
  { key: "finance",       label: "Revenue & Finance",   icon: "ri-line-chart-line",    group: "ERP & Finance", desc: "Revenue, recurring income, collections and outstanding balances over time." },

  { key: "reviews",       label: "Reviews",             icon: "ri-chat-3-line",        group: "Engagement",    desc: "Moderate user-submitted reviews across all listings." },

  { key: "ads",                label: "Advertisements",       icon: "ri-advertisement-line",     group: "Monetization", desc: "Create and manage banner advertisements and their performance." },
  { key: "ad-placements",      label: "Advertisement Placements", icon: "ri-layout-masonry-line",  group: "Monetization", desc: "Define where advertisements can appear across the site." },
  { key: "subscriptions",      label: "Subscriptions",        icon: "ri-bank-card-line",         group: "Monetization", desc: "Listing plan subscriptions, renewals and status." },
  { key: "plans",              label: "Listing Plans",        icon: "ri-price-tag-3-line",       group: "Monetization", desc: "Create and edit the plans shown on the public listing-plans page — pricing, highlights, comparison table and FAQs." },

  { key: "seo",           label: "SEO Settings",        icon: "ri-search-eye-line",    group: "System",        desc: "Per-page titles, meta descriptions and indexing rules." },
  { key: "media",         label: "Media Library",       icon: "ri-image-2-line",       group: "System",        desc: "Browse, upload, copy and delete the files stored in the site's uploads folder." },
  { key: "settings",      label: "System Settings",     icon: "ri-settings-3-line",    group: "System",        desc: "Global configuration — branding, contact details, limits and socials." },
];